import { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from 'expo-router';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useRouteMapModel } from '@/hooks/use-route-map-model';
import { loadMapsModule } from '@/components/route-planner/map/MapsModule';
import { RouteMapView } from '@/components/route-planner/map/RouteMapView';

/**
 * Full-screen route map: interactive pins for today's plan with a progress
 * chip. View-only — steps are ticked on the home card.
 */
export default function RouteMapScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { plan, progress, model, refresh } = useRouteMapModel();

  useFocusEffect(
    useCallback(() => {
      refresh();
    }, [refresh]),
  );

  const emptyMessage = !plan
    ? 'No active route for today.'
    : !loadMapsModule()
      ? 'The route map is not available in this app version yet. Update the app to see it.'
      : !model || model.pins.length === 0
        ? "None of today's stops could be placed on the map."
        : null;

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: palette.background }]}
      edges={['bottom']}
    >
      {emptyMessage ? (
        <View style={styles.centered}>
          <Text style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            {emptyMessage}
          </Text>
        </View>
      ) : (
        <View style={styles.container}>
          <RouteMapView model={model!} interactive showUnmappedChip />
          {progress && (
            <View style={[styles.progressChip, { backgroundColor: palette.cardBackground }]}>
              <Text
                style={[styles.progressText, { color: palette.primary, fontFamily: getBodyFont() }]}
              >
                {progress.completed} of {progress.total}
              </Text>
            </View>
          )}
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.xl,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  progressChip: {
    position: 'absolute',
    top: Spacing.md,
    right: Spacing.md,
    paddingVertical: Spacing.xxs,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.lg,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  progressText: {
    fontSize: 13,
    fontWeight: '600',
  },
});
